import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for managing application state in URL hash.
 * Enables browser back/forward navigation and page refresh state restoration.
 *
 * @param defaults - Default values for each state key
 * @param options.onBeforePop - Optional guard consulted on browser back/forward. Return
 *   false to cancel the navigation (the URL is restored and state left unchanged); return
 *   true (or omit the option) to allow it. Used to confirm leaving an in-progress upload —
 *   `beforeunload`/click handlers don't cover the back button on a same-document hash SPA.
 * @returns [state, updateState] - Current state and function to update it
 *
 * @example
 * const [hashState, setHashState] = useHashState({
 *   view: 'list' as const,
 *   id: null as string | null,
 *   tab: 'details' as const,
 * });
 *
 * // Update state (partial updates supported)
 * setHashState({ view: 'detail', id: '123' });
 */
export function useHashState<T extends Record<string, string | null>>(
  defaults: T,
  options?: { onBeforePop?: () => boolean }
): [T, (updates: Partial<T>) => void] {
  const defaultsRef = useRef(defaults);

  // Latest guard, kept in a ref so the popstate listener always sees the current one
  // without needing to re-subscribe.
  const onBeforePopRef = useRef(options?.onBeforePop);
  onBeforePopRef.current = options?.onBeforePop;

  // Parse hash string into state object
  const parseHash = useCallback((): T => {
    const hash = window.location.hash.slice(1); // Remove '#'
    if (!hash) return { ...defaultsRef.current };

    const params = new URLSearchParams(hash);
    const state = { ...defaultsRef.current };

    for (const key of Object.keys(defaultsRef.current)) {
      const value = params.get(key);
      if (value !== null) {
        (state as Record<string, string | null>)[key] = value;
      }
    }

    return state as T;
  }, []);

  const [state, setState] = useState<T>(() => parseHash());

  // The hash currently rendered — used to restore the URL when a back/forward is cancelled.
  const currentHashRef = useRef<string>(
    typeof window !== 'undefined' ? window.location.hash : ''
  );

  // Update state and sync to URL hash
  const updateState = useCallback((updates: Partial<T>) => {
    setState((prev: T) => {
      const newState = { ...prev, ...updates };

      // Build hash string, omitting null/empty values
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(newState)) {
        if (value != null && value !== '') {
          params.set(key, String(value));
        }
      }

      const hash = params.toString();
      const newUrl = hash
        ? `${window.location.pathname}${window.location.search}#${hash}`
        : `${window.location.pathname}${window.location.search}`;

      // Use pushState to enable back/forward navigation
      window.history.pushState(null, '', newUrl);
      currentHashRef.current = window.location.hash;

      return newState;
    });
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const guard = onBeforePopRef.current;
      if (guard && !guard()) {
        // Navigation cancelled (e.g. an upload is in progress and the user declined).
        // pushState does NOT fire popstate, so restoring the previous URL here is safe and
        // leaves rendered state untouched.
        const restore = currentHashRef.current;
        const url = restore
          ? `${window.location.pathname}${window.location.search}${restore}`
          : `${window.location.pathname}${window.location.search}`;
        window.history.pushState(null, '', url);
        return;
      }
      currentHashRef.current = window.location.hash;
      setState(parseHash());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [parseHash]);

  return [state, updateState];
}
